import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Input, InputGroup, InputGroupText} from 'reactstrap'
import {useShapesTemplateList} from '../../hooks/useShapesTemplateList'
import {useDraftShapes} from '../../hooks/useDraftShapes'
import {restoreDraftImages} from '../../utils/restoreDraftImages'

const ShapesPanel = ({runtime, onSelectDraft, onSelectShape}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const searchTimeoutRef = useRef(null)

    const {items, total, page, limit, loading, error, loadPage, setFilters} = useShapesTemplateList({limit: 12})

    const {drafts, loading: draftsLoading} = useDraftShapes()

    const handleSelectShape = useCallback(
        (template) => {
            if (!runtime?.loadDocument) return
            const raw = template.data
            if (raw == null) return
            const doc = typeof raw === 'string' ? JSON.parse(raw) : raw
            const meta = {
                id: template._id,
                name: template.name,
                description: template.description,
            }
            runtime.loadDocument(doc, meta)
            onSelectShape?.()
        },
        [runtime, onSelectShape],
    )

    const handleSelectDraft = useCallback(
        async (draft) => {
            if (!runtime?.loadDocument || !draft?.document) return
            runtime.loadDocument(draft.document)
            await restoreDraftImages(runtime, draft.id)
            onSelectDraft?.(draft.id)
        },
        [runtime, onSelectDraft],
    )

    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value
            setSearchQuery(value)
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
            searchTimeoutRef.current = setTimeout(() => {
                setFilters({name: value.trim()})
            }, 400)
        },
        [setFilters],
    )

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
        }
    }, [])

    const totalPages = Math.max(1, Math.ceil(total / limit))
    const canPrev = page > 1
    const canNext = page < totalPages

    const showDrafts = drafts.length > 0

    return (
        <section className="SidebarPanel" aria-label="Shapes">
            <header className="SidebarPanel__Header">
                <h2 className="SidebarPanel__Title">Shapes</h2>
            </header>

            <div className="SidebarPanel__Content">
                {/* Search input */}
                <div className="SidebarPanel__SearchWrap">
                    <InputGroup size="sm">
                        <InputGroupText>
                            <i className="fas fa-search" />
                        </InputGroupText>
                        <Input
                            type="text"
                            placeholder="Search shapes…"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            aria-label="Search shapes"
                        />
                    </InputGroup>
                </div>

                {/* Draft Shapes block */}
                {draftsLoading && <p className="SidebarPanel__Loading">Loading drafts…</p>}
                {!draftsLoading && showDrafts && (
                    <div className="SidebarPanel__Section">
                        <h3 className="SidebarPanel__SectionTitle">Draft Shapes</h3>
                        <div className="SidebarPanel__ShapesGrid">
                            {drafts.map((draft) => (
                                <button
                                    key={draft.id}
                                    type="button"
                                    className="SidebarPanel__ShapeThumb"
                                    onClick={() => handleSelectDraft(draft)}
                                    aria-label="Draft shape"
                                >
                                    {draft.previewUrl ? (
                                        <img src={draft.previewUrl} alt="" className="SidebarPanel__ShapeThumbImg" />
                                    ) : (
                                        <span className="SidebarPanel__ShapeThumbPlaceholder">Draft</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Saved Shapes block */}
                <div className="SidebarPanel__Section">
                    <h3 className="SidebarPanel__SectionTitle">Saved Shapes</h3>

                    {error && (
                        <p className="SidebarPanel__Error" role="alert">
                            {error.message}
                        </p>
                    )}

                    {loading && <p className="SidebarPanel__Loading">Loading…</p>}

                    {!loading && !error && (
                        <>
                            {items.length === 0 ? (
                                <p className="SidebarPanel__Empty">No shapes found.</p>
                            ) : (
                                <div className="SidebarPanel__ShapesGrid">
                                    {items.map((template) => (
                                        <button
                                            key={template._id}
                                            type="button"
                                            className="SidebarPanel__ShapeThumb"
                                            onClick={() => handleSelectShape(template)}
                                            aria-label={template.name || 'Shape'}
                                        >
                                            {template.preview_image ? (
                                                <img
                                                    src={template.preview_image}
                                                    alt=""
                                                    className="SidebarPanel__ShapeThumbImg"
                                                />
                                            ) : (
                                                <span className="SidebarPanel__ShapeThumbPlaceholder">No preview</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="SidebarPanel__Pagination">
                                    <button
                                        type="button"
                                        className="SidebarPanel__PaginationBtn"
                                        disabled={!canPrev}
                                        onClick={() => loadPage(page - 1)}
                                        aria-label="Previous page"
                                    >
                                        Prev
                                    </button>
                                    <span className="SidebarPanel__PaginationLabel">
                                        {page} / {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="SidebarPanel__PaginationBtn"
                                        disabled={!canNext}
                                        onClick={() => loadPage(page + 1)}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    )
}

export default ShapesPanel
